import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import TimePicker24 from "../components/TimePicker24";
import SearchableSelect from "../components/SearchableSelect";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, Clock, MapPin, Tag, MoreVertical, Copy, Globe2, CheckCircle2, XCircle, CircleDashed, ChevronLeft, ChevronRight, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { formatDateFr } from "../lib/dateFormat";
import EventDetailsDialog from "../components/EventDetailsDialog";
import DuplicateRecurrenceDialog from "../components/DuplicateRecurrenceDialog";
import AgendaSlotDialog from "../components/AgendaSlotDialog";
import AgendaSlotBand from "../components/AgendaSlotBand";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import StickyPageHeader from "../components/StickyPageHeader";
import NonWorkingDayBanner from "../components/NonWorkingDayBanner";
import RecurrenceFormSection, { RECURRENCE_EMPTY, toRecurrencePayload } from "../components/RecurrenceFormSection";
import { useNonWorkingDays } from "../hooks/useNonWorkingDays";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";

// Métadonnées visuelles des 3 statuts de réalisation d'une intervention.
// Le libellé et l'icône s'affichent sur chaque carte + dans le menu d'action.
const VALIDATION_META = {
  pending:   { label: "Planifiée",     color: "#0284C7", bg: "#0284C71A", icon: CircleDashed },
  completed: { label: "Effectuée",     color: "#059669", bg: "#0596691A", icon: CheckCircle2 },
  missed:    { label: "Non effectuée", color: "#DC2626", bg: "#DC26261A", icon: XCircle },
};
const getValidationMeta = (s) => VALIDATION_META[s] || VALIDATION_META.pending;

const EMPTY = {
  brand_id: "",
  site_id: "",
  beneficiary_id: "",
  intervention_category_id: "",
  date: (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })(),
  start_time: "09:00",
  end_time: "12:00",
  title: "",
  notes: "",
};

function startOfWeek(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7; // monday=0
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
// Format YYYY-MM-DD en heure LOCALE (pas UTC) — corrige le décalage de date
// entre la vue "jour" (qui utilise `cursor` à l'instant courant) et les vues
// "semaine" / "mois" (qui utilisent des dates à minuit local). `toISOString()`
// convertit en UTC et provoque un shift de ±1 jour en Europe.
function isoDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AgendaDepartements() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [events, setEvents] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [sites, setSites] = useState([]);
  const [shiftCats, setShiftCats] = useState([]);
  const [intCats, setIntCats] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [viewMode, setViewMode] = useState("week"); // "day" | "week" | "month"
  const [cursor, setCursor] = useState(new Date()); // pour day & month
  const [filterBrand, setFilterBrand] = useState(() => selectedBrandId || "");

  // Synchronise le filtre local avec le sélecteur d'en-tête (header)
  useEffect(() => { setFilterBrand(selectedBrandId || ""); }, [selectedBrandId]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [recurrence, setRecurrence] = useState(RECURRENCE_EMPTY);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [dupDialog, setDupDialog] = useState({ open: false, event: null });
  const [slots, setSlots] = useState([]);
  const [slotDialog, setSlotDialog] = useState({ open: false, editing: null, defaultDate: null });
  const [filters, setFilters] = useState({ q: "", site_id: "", category_id: "", beneficiary_id: "" });

  const canManage = ["admin", "manager", "gestionnaire"].includes(user?.role);
  const canManageSlots = ["admin", "manager"].includes(user?.role);

  // ─── Plage de dates selon la vue active ───────────────────────────────
  // Day view  → cursor uniquement
  // Week view → semaine de weekStart (7 jours)
  // Month view → tous les jours de la grille du mois (inclut quelques jours adjacents)
  const visibleDays = useMemo(() => {
    if (viewMode === "day") return [new Date(cursor)];
    if (viewMode === "week") return Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));
    // month : grille 6×7 commençant le lundi
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }).map((_, i) => addDays(gridStart, i));
  }, [viewMode, weekStart, cursor]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)),
    [weekStart]
  );
  const start_date = isoDate(visibleDays[0]);
  const end_date = isoDate(visibleDays[visibleDays.length - 1]);

  const load = async () => {
    try {
      const params = { start_date, end_date };
      if (filterBrand) params.brand_id = filterBrand;
      const brandOnly = filterBrand ? { brand_id: filterBrand } : {};
      const [evRes, benRes, siteRes, scRes, icRes, slotsRes] = await Promise.all([
        api.get("/department-agenda", { params }),
        api.get("/beneficiaries", { params: brandOnly }),
        api.get("/sites", { params: brandOnly }),
        api.get("/shift-categories", { params: brandOnly }),
        api.get("/intervention-categories", { params: brandOnly }),
        api.get("/agenda-slots", { params }).catch(() => ({ data: [] })),
      ]);
      setEvents(evRes.data || []);
      setBeneficiaries(benRes.data || []);
      setSites(siteRes.data || []);
      setShiftCats(scRes.data || []);
      setIntCats(icRes.data || []);
      setSlots(slotsRes.data || []);
    } catch {
      setEvents([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start_date, end_date, filterBrand]);

  // Jours non travaillés couvrant la plage visible + département (si filtré)
  const { byDate: nwdByDate } = useNonWorkingDays({
    startDate: start_date,
    endDate: end_date,
    brandId: filterBrand || undefined,
  });

  const openCreate = (date = null) => {
    setEditing(null);
    setForm({
      ...EMPTY,
      brand_id: filterBrand || brands[0]?.id || "",
      beneficiary_id: beneficiaries[0]?.id || "",
      date: date || EMPTY.date,
    });
    setRecurrence(RECURRENCE_EMPTY);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      brand_id: e.brand_id || "",
      site_id: e.site_id || "",
      beneficiary_id: e.beneficiary_id || "",
      intervention_category_id: e.intervention_category_id || e.shift_category_id || "",
      date: e.date || EMPTY.date,
      start_time: e.start_time || "09:00",
      end_time: e.end_time || "12:00",
      title: e.title || "",
      notes: e.notes || "",
    });
    setRecurrence(RECURRENCE_EMPTY);
    setOpen(true);
  };

  const save = async () => {
    if (!form.brand_id) { toast.error("Département requis"); return; }
    if (!form.beneficiary_id) { toast.error("Bénéficiaire requis"); return; }
    if (!form.site_id) { toast.error("Site requis"); return; }
    if (!form.intervention_category_id) { toast.error("Catégorie d'intervention requise"); return; }
    if (!form.date || !form.start_time || !form.end_time) { toast.error("Date et heures requises"); return; }
    const recPayload = toRecurrencePayload(recurrence);
    if (recurrence.recurrence_type !== "once" && !recurrence.end_date) {
      toast.error("Date de fin requise pour une récurrence");
      return;
    }
    if (recPayload && recPayload.end_date <= form.date) {
      toast.error("La date de fin de récurrence doit être postérieure à la date de l'intervention");
      return;
    }
    try {
      const payload = {
        ...form,
        site_id: form.site_id,
        intervention_category_id: form.intervention_category_id,
        shift_category_id: null,
      };
      let saved;
      if (editing) {
        const res = await api.put(`/department-agenda/${editing.id}`, payload);
        saved = res.data || { ...payload, id: editing.id };
        toast.success("Intervention mise à jour");
      } else {
        const res = await api.post("/department-agenda", payload);
        saved = res.data;
        toast.success("Intervention planifiée — les managers du département ont été notifiés");
      }
      // Récurrence : crée les occurrences supplémentaires à partir de l'intervention source
      if (recPayload && saved?.id) {
        try {
          const { data } = await api.post(`/department-agenda/${saved.id}/recurrence`, recPayload);
          if (data?.created > 0) {
            toast.success(`${data.created} occurrence(s) récurrente(s) créée(s)`);
          } else {
            toast.info("Aucune occurrence supplémentaire à créer (période ou jours non couverts)");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Récurrence non appliquée");
        }
      }
      setOpen(false);
      setRecurrence(RECURRENCE_EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette intervention ?")) return;
    try {
      await api.delete(`/department-agenda/${id}`);
      toast.success("Supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ─── Statut de réalisation (planifié / effectué / non effectué) ───────
  const [missedDialog, setMissedDialog] = useState({ open: false, event: null, reason: "" });

  const updateValidation = async (event, status, reason = null) => {
    try {
      const payload = { validation_status: status };
      if (status === "missed") payload.missed_reason = (reason || "").trim();
      await api.put(`/department-agenda/${event.id}/validation`, payload);
      toast.success(
        status === "completed" ? "Marquée effectuée"
        : status === "missed" ? "Marquée non effectuée"
        : "Statut réinitialisé"
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openMissedDialog = (event) => {
    setMissedDialog({
      open: true,
      event,
      reason: event.validation_status === "missed" ? (event.missed_reason || "") : "",
    });
  };
  const confirmMissed = async () => {
    const { event, reason } = missedDialog;
    if (!reason.trim()) {
      toast.error("Un motif est requis");
      return;
    }
    await updateValidation(event, "missed", reason);
    setMissedDialog({ open: false, event: null, reason: "" });
  };

  // Résout la catégorie d'un événement en priorisant la nouvelle collection
  // `intervention_categories` puis en retombant sur les anciennes `shift_categories`
  // (rétro-compat pour les interventions déjà planifiées avant la scission).
  const resolveEventCategory = (e) => {
    if (!e) return null;
    const iid = e.intervention_category_id;
    if (iid) {
      const found = intCats.find((c) => c.id === iid);
      if (found) return found;
    }
    const sid = e.shift_category_id;
    if (sid) {
      return shiftCats.find((c) => c.id === sid) || intCats.find((c) => c.id === sid) || null;
    }
    return null;
  };

  const filteredEvents = useQuickFilter(events, filters, {
    text: (e) => {
      const ben = beneficiaries.find((b) => b.id === e.beneficiary_id);
      const site = sites.find((s) => s.id === e.site_id);
      const cat = resolveEventCategory(e);
      return `${e.title || ""} ${e.description || ""} ${ben?.name || ""} ${site?.name || ""} ${cat?.name || ""}`;
    },
    predicates: {
      site_id: (e, value) => e.site_id === value,
      category_id: (e, value) => (e.intervention_category_id === value) || (e.shift_category_id === value),
      beneficiary_id: (e, value) => e.beneficiary_id === value,
    },
  });

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const d of visibleDays) map[isoDate(d)] = [];
    for (const e of filteredEvents) {
      if (map[e.date]) map[e.date].push(e);
    }
    Object.keys(map).forEach((k) => map[k].sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [filteredEvents, visibleDays]);

  // Plages horaires réservées ventilées par jour (filtre brand + occurrences)
  const slotsByDay = useMemo(() => {
    const map = {};
    for (const d of visibleDays) map[isoDate(d)] = [];
    for (const s of slots) {
      if (filterBrand && s.brand_id !== filterBrand) continue;
      const occ = s.occurrences || [];
      for (const day of occ) {
        if (map[day]) map[day].push(s);
      }
    }
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return map;
  }, [slots, visibleDays, filterBrand]);

  return (
    <div className="space-y-6" data-testid="agenda-departements-page">
      <StickyPageHeader testid="agenda-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Interventions programmées</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Agenda départements
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Planifiez des interventions par département / bénéficiaire — sans affecter de collaborateur.
            Les managers du département reçoivent une notification.
          </p>
        </div>
        {(canManage || canManageSlots) && (
          <div className="flex items-center gap-2 flex-wrap">
            {canManageSlots && (
              <Button
                onClick={() => setSlotDialog({ open: true, editing: null, defaultDate: null })}
                variant="outline"
                className="border-zinc-300 text-zinc-800 hover:bg-zinc-50 rounded-sm"
                data-testid="new-slot-btn"
              >
                <Plus size={14} className="mr-1" /> Nouvelle plage
              </Button>
            )}
            {canManage && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setRecurrence(RECURRENCE_EMPTY); } }}>
                <DialogTrigger asChild>
                  <Button onClick={() => openCreate()} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-sm" data-testid="new-agenda-btn">
                    <Plus size={14} className="mr-1" /> Nouvelle intervention
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editing ? "Modifier l'intervention" : "Nouvelle intervention"}</DialogTitle>
                  </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Département</Label>
                    <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v, site_id: "" })}>
                      <SelectTrigger data-testid="agenda-brand"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {brands.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`agenda-brand-opt-${b.id}`}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Bénéficiaire</Label>
                    <SearchableSelect
                      value={form.beneficiary_id}
                      onChange={(v) => setForm({ ...form, beneficiary_id: v })}
                      options={beneficiaries
                        .filter((b) => !form.brand_id || (b.brand_ids || []).includes(form.brand_id))
                        .map((b) => ({
                          value: b.id,
                          label: b.denomination,
                          keywords: `${b.denomination || ""} ${b.email || ""} ${b.phone || ""} ${b.city || ""}`,
                          hint: b.city || undefined,
                        }))}
                      placeholder="—"
                      searchPlaceholder="Dénomination, email, ville…"
                      emptyLabel="Aucun bénéficiaire trouvé."
                      allowClear
                      data-testid="agenda-beneficiary"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Intitulé</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Réunion mensuelle / Livraison / Visite…" data-testid="agenda-title" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="agenda-date" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Début</Label>
                    <TimePicker24 value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} data-testid="agenda-start" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Fin</Label>
                    <TimePicker24 value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} data-testid="agenda-end" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Site <span className="text-red-600">*</span></Label>
                    <SearchableSelect
                      value={form.site_id || ""}
                      onChange={(v) => setForm({ ...form, site_id: v })}
                      options={sites
                        .filter((s) => !form.brand_id || (s.brand_ids || []).includes(form.brand_id))
                        .map((s) => ({
                          value: s.id,
                          label: s.name,
                          keywords: `${s.name || ""} ${s.city || ""} ${s.address || ""} ${s.code || ""}`,
                          hint: s.city || undefined,
                        }))}
                      placeholder="Sélectionner un site…"
                      searchPlaceholder="Nom, ville, adresse…"
                      emptyLabel="Aucun site trouvé."
                      data-testid="agenda-site"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Catégorie d&apos;intervention <span className="text-red-600">*</span></Label>
                    <Select value={form.intervention_category_id || ""} onValueChange={(v) => setForm({ ...form, intervention_category_id: v })}>
                      <SelectTrigger data-testid="agenda-intcat"><SelectValue placeholder="Sélectionner une catégorie…" /></SelectTrigger>
                      <SelectContent>
                        {intCats
                          .filter((c) => {
                            if (!form.brand_id) return true;
                            const ids = (c.brand_ids && c.brand_ids.length > 0) ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
                            return ids.includes(form.brand_id);
                          })
                          .map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        {intCats.length === 0 && (
                          <SelectItem value="__none_available" disabled>
                            Aucune catégorie — créez-en dans Catégories › Interventions
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="agenda-notes" />
                </div>

                <RecurrenceFormSection
                  value={recurrence}
                  onChange={setRecurrence}
                  startDate={form.date}
                  testIdPrefix="interv-rec"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="agenda-save-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
            )}
          </div>
        )}
      </div>

      {/* Toolbar : nav + view toggle + filter */}
      <div className="bg-white border border-zinc-200 rounded-sm p-3 flex items-center gap-3 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (viewMode === "day") setCursor(addDays(cursor, -1));
            else if (viewMode === "week") setWeekStart(addDays(weekStart, -7));
            else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
          }}
          data-testid="agenda-prev"
        ><ChevronLeft size={14} /></Button>
        <span className="font-mono text-xs text-zinc-600" data-testid="agenda-range-label">
          {viewMode === "day" && `Jour : ${formatDateFr(cursor)}`}
          {viewMode === "week" && `Semaine du ${formatDateFr(weekDays[0])} au ${formatDateFr(weekDays[6])}`}
          {viewMode === "month" && cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (viewMode === "day") setCursor(addDays(cursor, 1));
            else if (viewMode === "week") setWeekStart(addDays(weekStart, 7));
            else setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
          }}
          data-testid="agenda-next"
        ><ChevronRight size={14} /></Button>

        {/* View toggle */}
        <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setViewMode("day")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${viewMode === "day" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="agenda-view-day-btn"
          >Jour</button>
          <button
            type="button"
            onClick={() => setViewMode("week")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-zinc-200 ${viewMode === "week" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="agenda-view-week-btn"
          >Semaine</button>
          <button
            type="button"
            onClick={() => setViewMode("month")}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-zinc-200 ${viewMode === "month" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="agenda-view-month-btn"
          >Mois</button>
        </div>

        <div className="flex-1" />
        <div className="min-w-[200px]">
          <Select value={filterBrand || "all"} onValueChange={(v) => setFilterBrand(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="agenda-filter-brand"><SelectValue placeholder="Tous départements" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous départements</SelectItem>
              {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <QuickSearchBar
        value={filters}
        onChange={setFilters}
        placeholder="Rechercher un événement (titre, description, bénéficiaire, site, catégorie)…"
        testid="agenda-search"
        filters={[
          { key: "site_id", label: "Site", type: "select", placeholder: "Tous les sites", options: sites.map((s) => ({ value: s.id, label: s.name })) },
          { key: "category_id", label: "Catégorie", type: "select", placeholder: "Toutes les catégories", options: intCats.map((c) => ({ value: c.id, label: c.name })) },
          { key: "beneficiary_id", label: "Bénéficiaire", type: "select", placeholder: "Tous les bénéficiaires", options: beneficiaries.map((b) => ({ value: b.id, label: b.name })) },
        ]}
      />
      </StickyPageHeader>

      {/* Day & Week views : bandes horizontales (un par jour) */}
      {viewMode !== "month" && (
      <div className="space-y-3">
        {visibleDays.map((d) => {
          const key = isoDate(d);
          const items = eventsByDay[key] || [];
          const isToday = key === isoDate(new Date());
          const nwdEntries = nwdByDate[key] || [];
          const isNonWorking = nwdEntries.length > 0;
          return (
            <div key={key} className={`bg-white border rounded-sm overflow-hidden ${isNonWorking ? "border-red-300" : isToday ? "border-[#7C3AED]" : "border-zinc-200"}`} data-testid={`agenda-day-${key}`}>
              {isNonWorking && (
                <NonWorkingDayBanner
                  entries={nwdEntries}
                  showBrands={!filterBrand}
                  brands={brands}
                  testIdPrefix="agenda-nwd"
                />
              )}
              <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/60">
                <div className="flex items-center gap-3">
                  <div className="text-center min-w-[44px]">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</p>
                    <p className="text-xl font-display font-bold tracking-tight text-zinc-900">{d.getDate()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{formatDateFr(d)}</p>
                    <p className="text-[11px] text-zinc-500">{items.length} {items.length > 1 ? "interventions" : "intervention"}</p>
                  </div>
                  {isToday && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#7C3AED] text-white rounded-sm ml-2">Aujourd&apos;hui</span>
                  )}
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => openCreate(key)}
                    aria-label={`Créer une intervention le ${key}`}
                    title="Créer une intervention ce jour"
                    data-testid={`agenda-add-${key}`}
                    className="h-7 w-7 flex items-center justify-center rounded-sm border border-zinc-200 text-zinc-600 hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED] transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                )}
              </div>
              <div className="p-3">
                {(slotsByDay[key] || []).length > 0 && (
                  <div className="mb-3 space-y-1.5" data-testid={`agenda-slots-${key}`}>
                    {(slotsByDay[key] || []).map((s) => (
                      <AgendaSlotBand
                        key={`${s.id}-${key}`}
                        slot={s}
                        categories={shiftCats}
                        canEdit={canManageSlots}
                        onEdit={(slot) => setSlotDialog({ open: true, editing: slot, defaultDate: key })}
                      />
                    ))}
                  </div>
                )}
                {items.length === 0 ? (
                  <p className="text-xs text-zinc-400 text-center py-6">Aucune intervention planifiée</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {items.map((e) => {
                      const ben = beneficiaries.find((b) => b.id === e.beneficiary_id);
                      const br = brands.find((b) => b.id === e.brand_id);
                      const st = sites.find((x) => x.id === e.site_id);
                      const cat = resolveEventCategory(e);
                      const vMeta = getValidationMeta(e.validation_status);
                      const VIcon = vMeta.icon;
                      return (
                        <div
                          key={e.id}
                          className="p-3 border-l-4 rounded-sm bg-zinc-50 hover:bg-zinc-100 text-xs"
                          style={{ borderColor: br?.color || "#7C3AED" }}
                          data-testid={`agenda-event-${e.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="font-semibold text-zinc-900 truncate text-sm">{e.title || ben?.denomination || "Intervention"}</p>
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                              style={{ color: vMeta.color, backgroundColor: vMeta.bg }}
                              data-testid={`agenda-event-status-${e.id}`}
                              title={e.validation_status === "missed" && e.missed_reason ? `Motif : ${e.missed_reason}` : vMeta.label}
                            >
                              <VIcon size={9} />
                              {vMeta.label}
                            </span>
                          </div>
                          <div className="space-y-1 font-mono text-[11px] text-zinc-700">
                            <p className="inline-flex items-center gap-1.5"><Clock size={10} className="text-zinc-400" /> {e.start_time}–{e.end_time}</p>
                            {br?.timezone && (() => {
                              const tz = formatTimezoneLabel(br.timezone);
                              return tz.full ? (
                                <p
                                  className="inline-flex items-center gap-1.5 text-zinc-500"
                                  title={timezoneFull(br.timezone)}
                                  data-testid={`agenda-event-tz-${e.id}`}
                                >
                                  <Globe2 size={10} className="text-zinc-400" /> {tz.full}
                                </p>
                              ) : null;
                            })()}
                            {br && (
                              <p className="inline-flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: br.color }} />
                                {br.name}
                              </p>
                            )}
                            {st && (
                              <p className="inline-flex items-center gap-1.5 truncate"><MapPin size={10} className="text-zinc-400" /> {st.name}</p>
                            )}
                            {cat && (
                              <p className="inline-flex items-center gap-1.5 truncate" style={{ color: cat.color }}>
                                <Tag size={10} /> {cat.name}
                              </p>
                            )}
                            {ben && <p className="text-zinc-700 truncate">Bénéf. : {ben.denomination}</p>}
                            {e.validation_status === "missed" && e.missed_reason && (
                              <p className="text-[10px] italic text-red-700 truncate" title={e.missed_reason} data-testid={`agenda-event-reason-${e.id}`}>
                                Motif : {e.missed_reason}
                              </p>
                            )}
                          </div>
                          <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-zinc-200">
                            <button
                              onClick={() => { setDetailsEvent(e); setDetailsOpen(true); }}
                              className="text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-700"
                              data-testid={`agenda-consult-${e.id}`}
                            >
                              <Eye size={10} /> Consulter
                            </button>
                            {canManage && (
                              <>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-700"
                                      aria-label="Marquer le statut"
                                      data-testid={`agenda-event-validation-${e.id}`}
                                    >
                                      <VIcon size={10} style={{ color: vMeta.color }} />
                                      Statut
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                      Marquer comme
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                      onClick={() => updateValidation(e, "pending")}
                                      data-testid={`agenda-mark-pending-${e.id}`}
                                    >
                                      <CircleDashed size={12} className="mr-2 text-[#0284C7]" /> Planifiée
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => updateValidation(e, "completed")}
                                      data-testid={`agenda-mark-completed-${e.id}`}
                                    >
                                      <CheckCircle2 size={12} className="mr-2 text-[#059669]" /> Effectuée
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => openMissedDialog(e)}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`agenda-mark-missed-${e.id}`}
                                    >
                                      <XCircle size={12} className="mr-2" /> Non effectuée…
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <button
                                  onClick={() => openEdit(e)}
                                  className="text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-100 inline-flex items-center gap-1 text-zinc-700"
                                  data-testid={`agenda-edit-${e.id}`}
                                >
                                  <Pencil size={10} /> Modifier
                                </button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="text-[10px] px-1.5 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-100 text-zinc-700"
                                      aria-label="Plus d'actions"
                                      data-testid={`agenda-event-more-${e.id}`}
                                    >
                                      <MoreVertical size={11} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => setDupDialog({ open: true, event: e })}
                                      data-testid={`agenda-event-duplicate-${e.id}`}
                                    >
                                      <Copy size={12} className="mr-2" /> Dupliquer / Récurrence
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => remove(e.id)}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`agenda-del-${e.id}`}
                                    >
                                      <Trash2 size={12} className="mr-2" /> Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Month view : grille 6×7 */}
      {viewMode === "month" && (
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="agenda-month-grid">
        <div className="grid grid-cols-7 border-b border-zinc-200 bg-zinc-50/60">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
            <div key={d} className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {visibleDays.map((d, i) => {
            const key = isoDate(d);
            const items = eventsByDay[key] || [];
            const isCurMonth = d.getMonth() === cursor.getMonth();
            const isToday = key === isoDate(new Date());
            const nwdEntries = nwdByDate[key] || [];
            const isNonWorking = nwdEntries.length > 0;
            return (
              <div
                key={i}
                className={`border-r border-b border-zinc-100 min-h-[100px] p-1.5 ${isNonWorking ? "" : isCurMonth ? "bg-white" : "bg-zinc-50/40 text-zinc-400"}`}
                style={isNonWorking ? {
                  background: `repeating-linear-gradient(45deg, ${(nwdEntries[0].color || "#DC2626")}18 0 8px, ${(nwdEntries[0].color || "#DC2626")}0A 8px 16px)`,
                } : undefined}
                data-testid={`agenda-month-day-${key}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[11px] font-mono ${isToday ? "text-white bg-[#7C3AED] rounded-sm px-1.5 py-0.5" : "text-zinc-600"}`}>
                    {d.getDate()}
                  </span>
                  {canManage && isCurMonth && !isNonWorking && (
                    <button
                      type="button"
                      onClick={() => openCreate(key)}
                      className="opacity-0 hover:opacity-100 hover:!opacity-100 group-hover:opacity-100 text-zinc-400 hover:text-[#7C3AED]"
                      aria-label={`Créer une intervention le ${key}`}
                      data-testid={`agenda-month-add-${key}`}
                    >
                      <Plus size={11} />
                    </button>
                  )}
                </div>
                {isNonWorking && (
                  <NonWorkingDayBanner
                    entries={nwdEntries}
                    compact
                    showBrands={false}
                    testIdPrefix="agenda-month-nwd"
                  />
                )}
                <div className="space-y-0.5">
                  {(slotsByDay[key] || []).slice(0, 2).map((s) => (
                    <div
                      key={`slot-${s.id}-${key}`}
                      className="rounded-sm border-l-2 px-1 py-0.5 text-[10px] font-mono truncate"
                      style={{ backgroundColor: `${s.color || "#7C3AED"}22`, borderColor: s.color || "#7C3AED" }}
                      title={`${s.title} • ${s.start_time}–${s.end_time}`}
                      data-testid={`agenda-month-slot-${s.id}-${key}`}
                    >
                      <span className="text-zinc-500">{s.start_time}</span>{" "}
                      <span className="text-zinc-700">{s.title}</span>
                    </div>
                  ))}
                  {items.slice(0, 3).map((e) => {
                    const br = brands.find((b) => b.id === e.brand_id);
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => { setDetailsEvent(e); setDetailsOpen(true); }}
                        className="w-full text-left px-1 py-0.5 rounded-sm bg-zinc-50 hover:bg-zinc-100 border-l-2 text-[10px] font-mono truncate"
                        style={{ borderColor: br?.color || "#7C3AED" }}
                        data-testid={`agenda-month-event-${e.id}`}
                      >
                        <span className="text-zinc-500">{e.start_time}</span>{" "}
                        <span className="text-zinc-900">{e.title || "Intervention"}</span>
                      </button>
                    );
                  })}
                  {items.length > 3 && (
                    <p className="text-[10px] font-mono text-zinc-500 pl-1">+{items.length - 3}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      <EventDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        event={detailsEvent}
        brand={detailsEvent ? brands.find((b) => b.id === detailsEvent.brand_id) : null}
        site={detailsEvent ? sites.find((s) => s.id === detailsEvent.site_id) : null}
        beneficiary={detailsEvent ? beneficiaries.find((b) => b.id === detailsEvent.beneficiary_id) : null}
        category={detailsEvent ? resolveEventCategory(detailsEvent) : null}
      />
      <DuplicateRecurrenceDialog
        open={dupDialog.open}
        onOpenChange={(v) => setDupDialog((s) => ({ ...s, open: v }))}
        kind="event"
        source={dupDialog.event ? {
          id: dupDialog.event.id,
          date: dupDialog.event.date,
          start_time: dupDialog.event.start_time,
          end_time: dupDialog.event.end_time,
          label: dupDialog.event.title || "Intervention",
        } : null}
        onSuccess={() => load()}
      />
      <AgendaSlotDialog
        open={slotDialog.open}
        onClose={() => setSlotDialog({ open: false, editing: null, defaultDate: null })}
        editing={slotDialog.editing}
        defaultBrandId={filterBrand || slotDialog.editing?.brand_id || brands[0]?.id || ""}
        defaultDate={slotDialog.defaultDate || isoDate(new Date())}
        brands={brands}
        categories={shiftCats}
        onSaved={() => load()}
        onDeleted={() => load()}
      />

      {/* Dialogue de motif pour une intervention marquée « non effectuée » */}
      <Dialog
        open={missedDialog.open}
        onOpenChange={(v) => !v && setMissedDialog({ open: false, event: null, reason: "" })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle size={16} className="text-[#DC2626]" />
              Marquer comme non effectuée
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {missedDialog.event && (
              <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm text-xs font-mono text-zinc-700">
                <p className="font-semibold text-zinc-900 mb-1">
                  {missedDialog.event.title || "Intervention"}
                </p>
                <p>{missedDialog.event.date} · {missedDialog.event.start_time}–{missedDialog.event.end_time}</p>
              </div>
            )}
            <div>
              <Label className="label-uppercase mb-1.5 block" htmlFor="agenda-missed-reason-input">
                Motif <span className="text-red-600">*</span>
              </Label>
              <Textarea
                id="agenda-missed-reason-input"
                value={missedDialog.reason}
                onChange={(ev) => setMissedDialog((s) => ({ ...s, reason: ev.target.value }))}
                placeholder="Ex : absence du bénéficiaire, annulation client, imprévu opérationnel…"
                rows={3}
                data-testid="agenda-missed-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMissedDialog({ open: false, event: null, reason: "" })}
              data-testid="agenda-missed-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmMissed}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
              data-testid="agenda-missed-confirm-btn"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
